import classNames from "classnames";
import {
  Category,
  HelpContent,
} from "../../../../application/domain/help/help-entity";
import "./category.scss";
import {
  CategoryObjectIds,
  HelpIcon,
} from "../../../../application/domain/help/const";
import { useRef } from "react";
import { useFocus } from "./use-focus";

export type CategoryProps = {
  categoryList: Category[];
  columnsNumber?: 1 | 2;
};

export const HelpContentLink: React.FC<{
  content: HelpContent;
  className?: string;
}> = ({ content, className }) => {
  return (
    <a
      href={`/help/contents/${content.slug}`}
      className={classNames("help-category-content-title", className)}
    >
      {content.title}
    </a>
  );
};

export const HelpCategoryTitle: React.FC<{
  category: Category;
}> = ({ category }) => {
  const Icon = HelpIcon[category.id];
  const ref = useRef<HTMLHeadingElement>(null);
  useFocus(ref);
  return (
    <>
      <h2 ref={ref} className="help-category-header">
        <span className="help-category-header-icon">
          {Icon ? <Icon /> : null}
        </span>
        <a id={category.title} href={`#${category.title}`}>
          {category.title}
        </a>
      </h2>
      {category?.metadata?.description ? (
        <div
          className="help-category-description"
          dangerouslySetInnerHTML={{ __html: category.metadata.description }}
        />
      ) : (
        <></>
      )}
    </>
  );
};

const HelpCategoryContent: React.FC<{
  categoryList: Category[];
  columnsNumber?: 1 | 2;
  objectId: string;
}> = ({ categoryList, columnsNumber = 2, objectId }) => {
  const category = categoryList.find((category) => category.id === objectId);
  return category && category.metadata.children.length > 0 ? (
    <div className="help-category-box">
      <HelpCategoryTitle category={category} />
      <ul
        className={classNames(
          "help-category-content",
          `help-category-content-columns-${columnsNumber}`,
        )}
      >
        {category.metadata.children.map((child) => (
          <li key={child.slug}>
            <HelpContentLink content={child} />
          </li>
        ))}
      </ul>
    </div>
  ) : null;
};

export const TroubleshootingContent: React.FC<CategoryProps> = ({
  categoryList,
  columnsNumber,
}) => {
  return (
    <HelpCategoryContent
      categoryList={categoryList}
      columnsNumber={columnsNumber}
      objectId={CategoryObjectIds.TROUBLESHOOTING_OBJECT_ID}
    />
  );
};

export const TemplateContent: React.FC<CategoryProps> = ({
  categoryList,
  columnsNumber,
}) => {
  return (
    <HelpCategoryContent
      categoryList={categoryList}
      columnsNumber={columnsNumber}
      objectId={CategoryObjectIds.TEMPLATE_OBJECT_ID}
    />
  );
};

export const BeginnerGuideContent: React.FC<CategoryProps> = ({
  categoryList,
  columnsNumber,
}) => {
  return (
    <HelpCategoryContent
      categoryList={categoryList}
      columnsNumber={columnsNumber}
      objectId={CategoryObjectIds.BEGINNER_GUIDE_OBJECT_ID}
    />
  );
};

export const TrainingContent: React.FC<CategoryProps> = ({
  categoryList,
  columnsNumber,
}) => {
  return (
    <HelpCategoryContent
      categoryList={categoryList}
      columnsNumber={columnsNumber}
      objectId={CategoryObjectIds.TRAINING_OBJECT_ID}
    />
  );
};

export const SearchFunctionContent: React.FC<CategoryProps> = ({
  categoryList,
  columnsNumber,
}) => {
  return (
    <HelpCategoryContent
      categoryList={categoryList}
      columnsNumber={columnsNumber}
      objectId={CategoryObjectIds.SEARCH_FUNCTION_OBJECT_ID}
    />
  );
};

export const NewFunctionContent: React.FC<CategoryProps> = ({
  categoryList,
  columnsNumber,
}) => {
  return (
    <HelpCategoryContent
      categoryList={categoryList}
      columnsNumber={columnsNumber}
      objectId={CategoryObjectIds.NEW_FUNCTION_OBJECT_ID}
    />
  );
};

export const VideoContent: React.FC<CategoryProps> = ({
  categoryList,
  columnsNumber,
}) => {
  return (
    <HelpCategoryContent
      categoryList={categoryList}
      columnsNumber={columnsNumber}
      objectId={CategoryObjectIds.VIDEO_OBJECT_ID}
    />
  );
};

export const ReleaseNoteContent: React.FC<CategoryProps> = ({
  categoryList,
  columnsNumber,
}) => {
  return (
    <HelpCategoryContent
      categoryList={categoryList}
      columnsNumber={columnsNumber}
      objectId={CategoryObjectIds.RELEASE_NOTE_OBJECT_ID}
    />
  );
};

export const ProductInformationContent: React.FC<CategoryProps> = ({
  categoryList,
  columnsNumber,
}) => {
  return (
    <HelpCategoryContent
      categoryList={categoryList}
      columnsNumber={columnsNumber}
      objectId={CategoryObjectIds.PRODUCT_INFORMATION_OBJECT_ID}
    />
  );
};

export const PaidOptionContent: React.FC<CategoryProps> = ({
  categoryList,
  columnsNumber,
}) => {
  return (
    <HelpCategoryContent
      categoryList={categoryList}
      columnsNumber={columnsNumber}
      objectId={CategoryObjectIds.PAID_OPTION_OBJECT_ID}
    />
  );
};

export const SecurityContent: React.FC<CategoryProps> = ({
  categoryList,
  columnsNumber,
}) => {
  return (
    <HelpCategoryContent
      categoryList={categoryList}
      columnsNumber={columnsNumber}
      objectId={CategoryObjectIds.SECURITY_OBJECT_ID}
    />
  );
};

export const ContractContent: React.FC<CategoryProps> = ({
  categoryList,
  columnsNumber,
}) => {
  return (
    <HelpCategoryContent
      categoryList={categoryList}
      columnsNumber={columnsNumber}
      objectId={CategoryObjectIds.CONTRACT_OBJECT_ID}
    />
  );
};

export const SupportContent: React.FC<CategoryProps> = ({
  categoryList,
  columnsNumber,
}) => {
  return (
    <HelpCategoryContent
      categoryList={categoryList}
      columnsNumber={columnsNumber}
      objectId={CategoryObjectIds.SUPPORT_OBJECT_ID}
    />
  );
};

export const MailLetterContent: React.FC<CategoryProps> = ({
  categoryList,
  columnsNumber,
}) => {
  return (
    <HelpCategoryContent
      categoryList={categoryList}
      columnsNumber={columnsNumber}
      objectId={CategoryObjectIds.MAIL_LETTER_OBJECT_ID}
    />
  );
};

export const GovtechTrendsContent: React.FC<CategoryProps> = ({
  categoryList,
  columnsNumber,
}) => {
  return (
    <HelpCategoryContent
      categoryList={categoryList}
      columnsNumber={columnsNumber}
      objectId={CategoryObjectIds.GOVTECH_TRENDS_OBJECT_ID}
    />
  );
};
